import React from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from 'components';
import { Button, Icon, Tooltip } from 'design-system-web';
import { HookFormInput, HookFormTextarea } from 'components/reactHookForm';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import type { ChargeCode } from 'domains-billing-web-mappers';
import { ChargeCodeSource, GroupType } from 'domains-billing-web-mappers';
import styles from '../styles.scss';

const DESCRIPTION_MAX_LENGTH = 200;
const DESCRIPTION_MIN_ROWS = 4;

type ChargeCodeFormProps = {
  chargeCodeData?: ChargeCode;
  error?: {
    severity: 'warning' | 'error';
    error: Error;
  };
  disabled?: boolean;
  onSubmit: (data: ChargeCode) => Promise<void>;
  onCancel: () => void;
};

export function ChargeCodeForm({
  chargeCodeData,
  error,
  disabled,
  onSubmit,
  onCancel,
}: ChargeCodeFormProps) {
  const { t } = useTranslation();

  const { control, handleSubmit, watch } = useForm<ChargeCode>({
    mode: 'onChange',
    values: {
      id: chargeCodeData?.id || '',
      chargeCode: chargeCodeData?.chargeCode || '',
      glCode: chargeCodeData?.glCode || '',
      description: chargeCodeData?.description || '',
      groupId: chargeCodeData?.groupId || '',
      groupType: chargeCodeData?.groupType || GroupType.ACTIVATE_CHANNEL,
      chargeCodeSource:
        chargeCodeData?.chargeCodeSource || ChargeCodeSource.Activate,
    },
  });

  const watchedChargeCode = watch('chargeCode');
  const watchedGlCode = watch('glCode');

  const hasIntegrationSource =
    chargeCodeData?.chargeCodeSource === ChargeCodeSource.Integration;
  const isSaveDisabled =
    !watchedGlCode?.trim() ||
    (!hasIntegrationSource && !watchedChargeCode?.trim()) ||
    disabled ||
    error?.severity === 'error';

  return (
    <>
      <div className={styles.editChargeCodeForm}>
        <fieldset
          className={cx(styles.fieldset, {
            [styles.integration]: hasIntegrationSource,
          })}
        >
          <label
            className={cx(styles.label, {
              [styles.integration]: hasIntegrationSource,
            })}
          >
            <span className={styles.labelText}>
              {t('abp.productsServices.editChargeCode.chargeCode')}
              {!hasIntegrationSource && (
               <span className="ml-0.5 text-[#DA5A4E]">*</span>
              )}
            </span>
            {hasIntegrationSource && (
              <Tooltip
                placement="right"
                className="max-w-[310px]"
                TooltipComponent={t(
                  'abp.productsServices.editChargeCode.updateOnlyViaThirdPartySystem'
                )}
              >
                <Icon
                  name="info-circle"
                  size="small"
                  className="w-3 h-3 text-gray-500"
                />
              </Tooltip>
            )}
          </label>
          {hasIntegrationSource ? (
            <div className={cx(styles.staticValue, styles.integration)}>
              {chargeCodeData?.chargeCode || ''}
            </div>
          ) : (
            <HookFormInput
              control={control}
              name="chargeCode"
              type="text"
              isRequired
              testId="chargeCode"
              disabled={error?.severity === 'error'}
            />
          )}
        </fieldset>

        <fieldset className={styles.fieldset}>
          <label className={styles.label}>
            {t('abp.productsServices.editChargeCode.glCode')}
            <span className="ml-0.5 text-[#DA5A4E]">*</span>
          </label>
          <HookFormInput
            control={control}
            name="glCode"
            type="text"
            isRequired
            testId="glCode"
            disabled={error?.severity === 'error'}
          />
        </fieldset>

        <fieldset className={styles.fieldset}>
          <label className={styles.label}>
            {t('abp.productsServices.editChargeCode.description')}
          </label>
          <HookFormTextarea
            control={control}
            name="description"
            maxLength={DESCRIPTION_MAX_LENGTH}
            showLengthIndicator
            minRows={DESCRIPTION_MIN_ROWS}
            disabled={error?.severity === 'error'}
            containerClassName={
              error?.severity === 'error' ? styles.disabledTextarea : undefined
            }
            testId="description"
          />
        </fieldset>
      </div>

      {error && <ErrorMessage error={error.error.message} />}
      
      <div className={styles.formButtons}>
        <Button
          className={styles.button}
          variant="primary"
          size="large"
          disabled={isSaveDisabled}
          onClick={handleSubmit(onSubmit)}
        >
          {t('abp.productsServices.editChargeCode.save')}
        </Button>
        <Button
          className={styles.button}
          variant="secondary"
          size="large"
          onClick={onCancel}
          disabled={disabled}
        >
          {t('abp.productsServices.editChargeCode.cancel')}
        </Button>
      </div>
    </>
  );
}